import { Box, Grid, LinearProgress, Typography } from '@material-ui/core';
import { Field, useField, useForm, useFormState } from 'react-final-form';
import React, { FC, useContext, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import uploadcare from 'uploadcare-widget';
import { isEmpty } from 'lodash';
import uploadcareTabEffects from 'uploadcare-widget-tab-effects';
import { FormApi } from 'final-form';
import { useShopConfig, useShopConfigV2 } from '../../hooks/shopConfig';
import { useIsMobile } from '../../hooks/useIsMobile';
import {
  ImageFileData,
  SellerImageCategory,
  SellerImageCategoryContentfulConfig,
  SellerImageCategoryForCondition,
} from '../../types/photos/photos';
import { EditListingPageState, setImagesApiError } from './EditListingPage.duck';
import * as validators from '../../util/validators';
import {
  Error as ErrorComponent,
  IconEdit,
  IconStar,
  IconSubtractFilled,
  Menu,
  MenuContent,
  MenuItem,
  MenuLabel,
  PhotoTipsModal,
  ResponsiveImage,
  TypographyWrapper,
  ValidationError,
} from '../../components';
import { InlineTextButton } from '../../components/Button/Button';
import { Conditions } from '../../shopConfig/filters/condition';
import { defaultTreetStyles } from '../../shopConfig/config';
import { Image } from '../../types/sharetribe/image';
import { handle } from '../../util/helpers';
import Heading from './Heading';
import { UploadcareFile } from '../../types/uploadcare/file';
import * as log from '../../util/log';
import { getUploadcareImagePreviewUrl, ImageSource } from '../../util/uploadcare';
import MenuIcon from '../../components/ManageListingCard/MenuIcon';
import { ListingFlowFormField } from './ListingFlowTypes';
import { decimalToFraction } from '../../util/utilityFunctions';
import IconLightBulb from '../../components/Icons/IconLightBulb/IconLightBulb';
import { useListingFlowConfig } from '../../hooks/useListingFlowConfig';
import { useCurrentUserPermissions } from '../../hooks/useUserPermissions';
import DefaultBackgroundRemovalModal from '../../components/BackgroundRemovalModal/DefaultBackgroundRemovalModal';
import EditImageModal from '../../components/EditImageModal/EditImageModal';
import { OriginalAndBackgroundRemovedImage } from '../../types/uploadcare/backgroundRemovalTypes';
import { convertBase64toBlob } from '../../util/data';
import * as heap from '../../util/heap';
import AppContext from '../../context/AppContext';
import ChooseCoverPhoto from './ChooseCoverPhoto';
import { useOwnListingVersions } from './hooks/useOwnListingVersions';
import { FindItemMethod, ListingItemType } from '../../types/sharetribe/listing';
import { useIsBackgroundRemovalEnabled } from './hooks/useIsBackgroundRemovalEnabled';
import QRCode from './QRCode';
import { isDev, isTest } from '../../util/envHelpers';
import { getStockImagesForListing } from '../../util/listings/listingImages';
import { ModalType } from '../../ducks/modal.duck';
import { useActiveModal } from '../../hooks/useActiveModal';
import css from './EditListingPage.module.css';

const DEFAULT_PROGRESS_PERCENTAGE = 5;

const UPLOADCARE_COMPONENT_ID = 'uploader-placeholder';
const MAX_FILE_SIZE_IN_PIXELS = 1024 * 1024 * 20; // 20 MB
const MAX_FILE_SIZE_EXCEEDED_ERROR_MESSAGE = 'Max file size exceeded';

const MOBILE_IMAGES_CAROUSEL_ID = 'mobile-images-carousel';

const maxFileSizeValidator = (fileInfo: UploadcareFile) => {
  if (fileInfo.size && fileInfo.size > MAX_FILE_SIZE_IN_PIXELS) {
    throw new Error(MAX_FILE_SIZE_EXCEEDED_ERROR_MESSAGE);
  }
};

const getNumRequiredPhotos = (sellerImageCategories: SellerImageCategoryForCondition): number =>
  Object.values(sellerImageCategories).filter((categoryConfig) => categoryConfig?.required).length;

const getImageCategoriesForCondition = (
  sellerImageCategories: SellerImageCategoryContentfulConfig,
  condition: Conditions
): SellerImageCategoryForCondition =>
  Object.keys(sellerImageCategories).reduce((acc, categoryName) => {
    const categoryConfig = sellerImageCategories[categoryName as SellerImageCategory];
    const conditionsToRequire = categoryConfig?.conditionsToRequire;
    return {
      ...acc,
      [categoryName]: { ...categoryConfig, required: conditionsToRequire?.includes(condition) },
    };
  }, {});

const validatePhotos = (
  sellerImageCategories: SellerImageCategoryContentfulConfig,
  condition: Conditions,
  images: (Image | ImageFileData)[],
  isBrand: boolean
) => {
  const shouldValidateRequiredPhotos = !isDev && !isTest && !isBrand;
  // Final Form expects an undefined value for a successful validation
  if (!shouldValidateRequiredPhotos) return undefined;

  const requiredImageCategories = getImageCategoriesForCondition(sellerImageCategories, condition);
  const numRequiredPhotos = getNumRequiredPhotos(requiredImageCategories);
  return validators.minLength('Please upload all the required photos.', numRequiredPhotos)(images);
};

const editPhoto = (imageRatio: any, image: UploadcareFile, index: number, form: FormApi) => {
  const { top, bottom } = decimalToFraction(imageRatio) as any;
  const file = uploadcare.fileFrom('uploaded', image.cdnUrl);
  const panel = uploadcare.openDialog(file, '', {
    tabs: ['preview'],
    multiple: false,
    crop: [`${bottom}:${top}`, 'free'].join(),
    effects: 'crop, rotate, mirror, flip, enhance',
    // will retry upload if fails twice
    retryAttempts: 2,
    // 2 seconds between 1st and 2nd retry
    retryBaseTimeout: 2000,
  });
  panel.done((newFile: any) => {
    newFile.done((fileInfo: UploadcareFile) => {
      form.mutators.update(ListingFlowFormField.Images, index, fileInfo);
    });
  });
};

const buildImageWrapperStyle = (imageRatio: number, isMobile: boolean) => {
  const maxWidth = 120;

  return isMobile
    ? {
        width: `${maxWidth}px`,
        height: `${maxWidth * imageRatio}px`,
      }
    : {
        width: '80vw',
        height: `${80 * imageRatio}vw`,
        maxWidth: `${maxWidth}px`,
        maxHeight: `${maxWidth * imageRatio}px`,
        cursor: 'pointer',
      };
};

// TODO (anniew|TREET-1865) Clean up and type images
export const orderSharetribeImages = (images: any[], featuredImageId?: string) => {
  if (!images) {
    return null;
  }

  let firstImage: any = null;
  if (featuredImageId) {
    const image = images.find(
      // imageId is set if the image has not been saved to the listing yet
      (i) => i.id.uuid === featuredImageId || i.imageId?.uuid === featuredImageId
    );
    if (image) {
      firstImage = image;
    }
  }

  if (firstImage) {
    const orderedImages = images.filter((i) =>
      firstImage.imageId
        ? i.imageId?.uuid !== firstImage.imageId?.uuid
        : i.id.uuid !== firstImage.id.uuid
    );
    orderedImages.unshift(firstImage);
    return orderedImages;
  }
  return images;
};

export const orderUploadcareImages = (images: UploadcareFile[], featuredImageId?: string) => {
  const firstImage = featuredImageId
    ? images.find((image) => image.uuid === featuredImageId)
    : null;

  if (firstImage) {
    const orderedImages = images.filter((image) => image.uuid !== firstImage.uuid);
    orderedImages.unshift(firstImage);
    return orderedImages;
  }

  return images;
};

const PhotosError: FC = () => {
  const { imagesApiError } = useSelector<any>(
    (state) => state.EditListingPage
  ) as EditListingPageState;

  let errorString;
  if (imagesApiError) {
    errorString =
      'Whoops, something went wrong. Please check that the image isn’t over 20 MB and try again.';
  }

  return errorString ? (
    <Box mt={2}>
      <ErrorComponent> {errorString}</ErrorComponent>
    </Box>
  ) : null;
};

interface ImageMenuProps {
  image: UploadcareFile;
  index: number;
  onEditPhotoClicked: () => void;
}

const ImageMenu: FC<ImageMenuProps> = (props) => {
  const { image, index, onEditPhotoClicked } = props;
  const { treetId } = useContext(AppContext);
  const isMobile = useIsMobile();
  const form = useForm();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { shopName } = useShopConfigV2();
  const { currentListing } = useOwnListingVersions();

  const featuredImageId = useField(ListingFlowFormField.FeaturedImageId)?.input?.value;

  const deletePhotoAction = (
    <MenuItem key="delete-photo">
      <Box px={1}>
        <InlineTextButton
          rootClassName={css.menuItem}
          onClick={() => {
            heap.trackDeleteImage(
              currentListing.id?.uuid,
              treetId,
              image.isBackgroundRemoved || false,
              shopName
            );
            if (image.uuid === featuredImageId) {
              form.change(ListingFlowFormField.FeaturedImageId, undefined);
            }
            form.mutators.remove(ListingFlowFormField.Images, index);
          }}
        >
          <Box display="flex" flexDirection="row" alignItems="center" justifyContent="left">
            <IconSubtractFilled className={css.iconRemove} />
            <TypographyWrapper
              variant="body2"
              component="span"
              {...(isMobile && { typographyOverrides: { style: { fontSize: '12px' } } })}
            >
              Delete Photo
            </TypographyWrapper>
          </Box>
        </InlineTextButton>
      </Box>
    </MenuItem>
  );

  const editPhotoAction = (
    <MenuItem key="edit-photo">
      <Box px={1}>
        <InlineTextButton rootClassName={css.menuItem} onClick={onEditPhotoClicked}>
          <Box display="flex" flexDirection="row" alignItems="center" justifyContent="left">
            <IconEdit className={css.iconAction} />
            <TypographyWrapper
              component="span"
              variant="body2"
              {...(isMobile && { typographyOverrides: { style: { fontSize: '12px' } } })}
            >
              Edit Photo
            </TypographyWrapper>
          </Box>
        </InlineTextButton>
      </Box>
    </MenuItem>
  );

  return (
    <div className={css.menuContainer}>
      <Menu
        className={css.menu}
        contentPosition="left"
        useArrow={false}
        onToggleActive={(isOpen: boolean) => {
          setIsMenuOpen(isOpen);
        }}
        isOpen={isMenuOpen}
        mobileMenuContentStyleOverrides={{ left: -68 }}
      >
        <MenuLabel
          className={css.menuLabel}
          isOpenClassName={css.iconWrapperIsOpen}
          ariaLabel="Photo Action Menu"
        >
          <div className={css.iconWrapper}>
            <MenuIcon />
          </div>
        </MenuLabel>
        <MenuContent rootClassName={css.menuContent}>
          {editPhotoAction}
          {deletePhotoAction}
        </MenuContent>
      </Menu>
    </div>
  );
};

const CoverPhotoLabel: FC = () => (
  <Box
    display="flex"
    alignItems="center"
    justifyContent="center"
    width="100%"
    style={{
      backgroundColor: defaultTreetStyles.gray10,
      position: 'relative',
      top: '-24px',
      opacity: '0.8',
    }}
    py="4px"
    mb="-32px"
  >
    <IconStar className={css.iconAction} />
    <Typography variant="body2" style={{ color: defaultTreetStyles.gray80 }}>
      Cover Photo
    </Typography>
  </Box>
);

interface UploadedImageCardProps {
  image: UploadcareFile;
  index: number;
  onUploadedImageClick: () => void;
}

const UploadedImageCard: FC<UploadedImageCardProps> = (props) => {
  const { image, index, onUploadedImageClick } = props;
  const { imageRatio } = useShopConfigV2();
  const isMobile = useIsMobile();
  const featuredImageID = useField(ListingFlowFormField.FeaturedImageId)?.input?.value;

  const isCoverPhoto = image.uuid === featuredImageID;

  return (
    <>
      <ImageMenu image={image} index={index} onEditPhotoClicked={onUploadedImageClick} />
      <div
        className={css.imageThumbnail}
        style={buildImageWrapperStyle(imageRatio, isMobile)}
        onClick={onUploadedImageClick}
      >
        <ResponsiveImage alt={image.name} src={getUploadcareImagePreviewUrl(image.cdnUrl)} />
      </div>
      {isCoverPhoto && <CoverPhotoLabel />}
    </>
  );
};

interface UploadedImagesProps {
  images: UploadcareFile[];
  openEditImageModal?: (uploadcareFile: UploadcareFile) => void;
}

const UploadedImages: FC<UploadedImagesProps> = (props) => {
  const { images, openEditImageModal } = props;
  const isBackgroundRemovalEnabled = useIsBackgroundRemovalEnabled();
  const { imageRatio } = useShopConfigV2();
  const isMobile = useIsMobile();
  const form = useForm();

  const allImagesFormValue = useField(ListingFlowFormField.AllImages)?.input?.value || [];
  const allImagesUuids = allImagesFormValue.map(
    (uploadcareImage: UploadcareFile) => uploadcareImage.uuid
  );

  const openEditImageModalOrUploadcareEditModal = (image: UploadcareFile, index: number) => {
    const doesImageHaveLinkedUuid = !!image.linkedUuid;
    const doesLinkedRemovedBackgroundImageExist = allImagesUuids.indexOf(image.linkedUuid) !== -1;
    const shouldOpenEditImageModal =
      isBackgroundRemovalEnabled &&
      ((!doesImageHaveLinkedUuid && !image.isBackgroundRemoved) ||
        doesLinkedRemovedBackgroundImageExist);

    return shouldOpenEditImageModal && openEditImageModal
      ? openEditImageModal(image)
      : editPhoto(imageRatio, image, index, form);
  };

  return isMobile ? (
    <Box
      id={MOBILE_IMAGES_CAROUSEL_ID}
      display="flex"
      flexDirection="row"
      width="100%"
      style={{ overflowX: 'auto' }}
    >
      {images.map((image: UploadcareFile, index: number) => (
        <Box key={image.uuid} m={1}>
          <UploadedImageCard
            image={image}
            index={index}
            onUploadedImageClick={() => openEditImageModalOrUploadcareEditModal(image, index)}
          />
        </Box>
      ))}
    </Box>
  ) : (
    <Grid container direction="row" spacing={4} style={{ margin: 0 }}>
      {images.map((image: UploadcareFile, index: number) => (
        <Grid item key={image.uuid} md="auto" sm="auto" xs="auto">
          <UploadedImageCard
            image={image}
            index={index}
            onUploadedImageClick={() => openEditImageModalOrUploadcareEditModal(image, index)}
          />
        </Grid>
      ))}
    </Grid>
  );
};

interface ImageUploaderAPIProps {
  openEditImageModal?: (uploadcareFile: UploadcareFile) => void;
}

const ImageUploaderAPI: FC<ImageUploaderAPIProps> = (props) => {
  const { openEditImageModal } = props;
  const dispatch = useDispatch();
  const { imagesApiError } = useSelector<any>(
    (state) => state.EditListingPage
  ) as EditListingPageState;
  const { sellerImageCategoriesOptions } = useListingFlowConfig();
  const { sellerImageCategoriesJson: sellerImageCategories } = sellerImageCategoriesOptions || {};
  const { isBrand } = useCurrentUserPermissions();

  const form = useForm();
  let images = useField(ListingFlowFormField.Images)?.input?.value || [];
  const featuredImageID = useField(ListingFlowFormField.FeaturedImageId)?.input?.value;

  useEffect(() => {
    uploadcare.registerTab('preview', uploadcareTabEffects);
    const panel = uploadcare.openPanel(`#${UPLOADCARE_COMPONENT_ID}`, images, 'file', {
      imagesOnly: true,
      tabs: ['preview', 'file', 'camera'],
      validators: [maxFileSizeValidator],
      multiple: true,
      previewStep: true,
    });
    panel.fileColl.onAdd.add((filePromise: any) => {
      dispatch(setImagesApiError(null));
      filePromise.done((fileInfo: UploadcareFile) => {
        form.mutators.concat(ListingFlowFormField.Images, [fileInfo]);
        form.mutators.concat(ListingFlowFormField.AllImages, [fileInfo]);
      });
      filePromise.fail(
        (
          errorMessage: string,
          fileInfo: UploadcareFile,
          errorInfo: { code: string; message: string }
        ) => {
          dispatch(setImagesApiError(errorMessage));
          if (errorMessage !== MAX_FILE_SIZE_EXCEEDED_ERROR_MESSAGE) {
            log.error(
              new Error('Uploadcare image upload failed'),
              'uploadcare-image-upload-failed',
              {
                images,
                errorMessage,
                fileInfo,
                errorInfo: JSON.stringify(errorInfo),
              }
            );
          }
        }
      );
      panel.resolve();
    });
  }, [images, imagesApiError]);

  useEffect(() => {
    images = orderUploadcareImages(images, featuredImageID);
    images.forEach((image: UploadcareFile, index: number) => {
      form.mutators.update(ListingFlowFormField.Images, index, image);
    });
  }, [featuredImageID]);

  return (
    <>
      <Field
        name={ListingFlowFormField.Images}
        component="input"
        validate={(_value, allValues: any) =>
          validatePhotos(sellerImageCategories, allValues.condition, allValues.images, isBrand)
        }
        className={css.hiddenInput}
      >
        {({ input, meta }) => (
          <>
            {/* Setting id here so that form can scroll to it on error */}
            <input
              {...input}
              id={ListingFlowFormField.Images}
              className={css.hiddenInput}
              type="hidden"
            />
            <ValidationError fieldMeta={meta} />
          </>
        )}
      </Field>
      <Box
        {...(isEmpty(images) && { mb: 2 })}
        className={css.imageUploaderAPIContainer}
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
      >
        <div className="uploaderContainer">
          <Box
            id={UPLOADCARE_COMPONENT_ID}
            className={css.uploaderPlaceholder}
            height="250px"
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            Uploading. Please wait...
          </Box>
        </div>
        <UploadedImages images={images} openEditImageModal={openEditImageModal} />
      </Box>
    </>
  );
};

interface ChoosePhotosProps {
  imageCategoriesForCondition: SellerImageCategoryForCondition;
  openEditImageModal?: (uploadcareFile: UploadcareFile) => void;
}

const ChoosePhotos: FC<ChoosePhotosProps> = (props) => {
  const { imageCategoriesForCondition, openEditImageModal } = props;
  const imagesFormValue = useField(ListingFlowFormField.Images)?.input?.value || [];
  const isBackgroundRemovalEnabled = useIsBackgroundRemovalEnabled();
  const isMobile = useIsMobile();
  const { isBrand } = useCurrentUserPermissions();

  const numRequiredPhotos = getNumRequiredPhotos(imageCategoriesForCondition);
  const numUploadedPhotos = imagesFormValue?.length || 0;
  const numRequiredPhotosUploaded = Math.min(numUploadedPhotos, numRequiredPhotos);
  const progressPercentage = Math.max(
    (numRequiredPhotosUploaded / numRequiredPhotos || 0) * 100,
    DEFAULT_PROGRESS_PERCENTAGE
  );

  return (
    <Box display="flex" flex="3" flexDirection="column" width="100%">
      <Box display="flex" flexDirection="row" justifyContent="flex-start">
        <Box p={1}>
          <LinearProgress
            className={css.progressBar}
            color="primary"
            variant="determinate"
            value={progressPercentage}
            aria-label="Photo Upload Progress Bar"
          />
        </Box>
        <Typography variant="body2" style={{ fontWeight: 'bold' }}>
          {isBrand
            ? 'Uploaded photos are not required for brands, unless there are no stock photos'
            : `${numRequiredPhotosUploaded}/${numRequiredPhotos} Required Photos Uploaded`}
        </Typography>
      </Box>
      <ImageUploaderAPI openEditImageModal={openEditImageModal} />
      {!!numUploadedPhotos && (
        <Box display="flex" flexDirection="row" justifyContent="center" py={2}>
          <IconLightBulb className={css.iconLightBulb} />
          <Box display="flex" flexDirection="column" justifyContent="center">
            <TypographyWrapper
              variant="body1"
              typographyOverrides={{ style: { fontWeight: 'bold' } }}
            >
              {isMobile ? 'Tap' : 'Click'} any photo to
              {isBackgroundRemovalEnabled ? ' edit or remove the background' : ' crop or rotate'}
            </TypographyWrapper>
            <TypographyWrapper variant="body1">
              Listings that look great sell faster!
            </TypographyWrapper>
          </Box>
        </Box>
      )}
    </Box>
  );
};

interface PhotoRequirementsProps {
  imageCategoriesForCondition: SellerImageCategoryForCondition;
}

const PhotoRequirements: FC<PhotoRequirementsProps> = (props) => {
  const { imageCategoriesForCondition } = props;
  const numRequiredPhotos = getNumRequiredPhotos(imageCategoriesForCondition);
  const form = useForm();
  const listingCondition = form.getFieldState('condition')?.value;

  useEffect(() => {
    if (listingCondition) {
      form.change(ListingFlowFormField.NumRequiredImages, numRequiredPhotos);
    }
  }, [listingCondition]);

  return (
    <Box>
      <Box m={2}>
        <Typography variant="body2" style={{ fontWeight: 'bold' }}>
          Photos To Upload
        </Typography>
      </Box>
      <>
        <Field name={ListingFlowFormField.NumRequiredImages} component="input" type="hidden" />
        {Object.values(imageCategoriesForCondition)
          // sort values of requiredImageCategories so that required photos show up first
          .sort((a, b) => (a.required && !b.required ? -1 : 1))
          .map((categoryConfig) => (
            <Box key={categoryConfig.label} mx={2} my={1}>
              <Typography variant="body1">
                {`• ${categoryConfig.label} `}
                {categoryConfig?.required && <span className={css.requiredLabel}>(required)</span>}
              </Typography>
            </Box>
          ))}
      </>
      <QRCode />
    </Box>
  );
};

const PhotosUpload: FC = () => {
  const isMobile = useIsMobile();
  const { treetId } = useContext(AppContext);
  const { chooseStockImagesOption, imageRatio, shopName } = useShopConfig();
  const { photoTipsTitle, photoTipOptions, sellerImageCategoriesOptions } = useListingFlowConfig();
  const { sellerImageCategoriesJson: sellerImageCategories } = sellerImageCategoriesOptions || {};
  const form = useForm();
  const imagesFormValue = useField(ListingFlowFormField.Images)?.input?.value || [];
  const conditionFormValue = useField(ListingFlowFormField.Condition)?.input?.value;
  const allImagesFormValue = useField(ListingFlowFormField.AllImages)?.input?.value || [];
  const stockImagesFormValue = useField(ListingFlowFormField.StockPhotos)?.input?.value || [];

  const { currentListing } = useOwnListingVersions();

  const { publicData } = currentListing.attributes;

  const { imageSource, images } = publicData || {};

  const hasNoListingImages = isEmpty(images) && isEmpty(currentListing?.images);

  const { values } = useFormState();
  const { itemType } = values;
  const isTradeIn = itemType === ListingItemType.TradeIn;

  const {
    isModalOpen: isPhotoTipsModalOpen,
    openModal: openPhotoTipsModal,
    closeModal: closePhotoTipsModal,
  } = useActiveModal(ModalType.PhotoTips);
  const {
    isModalOpen: isBackgroundRemovalModalOpen,
    openModal: openBackgroundRemovalModal,
    closeModal: closeBackgroundRemovalModal,
  } = useActiveModal(ModalType.BackgroundRemoval);
  const {
    isModalOpen: isEditPhotoModalOpen,
    openModal: openEditPhotoModal,
    closeModal: closeEditPhotoModal,
  } = useActiveModal(ModalType.EditPhoto);

  const [editModalImage, setEditModalImage] = useState<UploadcareFile | undefined>(undefined);
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);
  const [didUploadBackgroundRemovedImageError, setDidUploadBackgroundRemovedImageError] =
    useState(false);

  const imageCategoriesForCondition = getImageCategoriesForCondition(
    sellerImageCategories,
    conditionFormValue
  );
  const imageUuids = imagesFormValue.map((uploadcareImage: UploadcareFile) => uploadcareImage.uuid);
  const imageIndex = imageUuids.indexOf(editModalImage?.uuid);
  const allImagesUuids = allImagesFormValue.map(
    (uploadcareImage: UploadcareFile) => uploadcareImage.uuid
  );

  const hasStockImages = !isEmpty(
    getStockImagesForListing(currentListing, chooseStockImagesOption)
  );

  const hasNoStockImagesOnListing = !hasStockImages || isEmpty(stockImagesFormValue);

  const shouldShowChooseCoverPhoto =
    (imageSource === ImageSource.Uploadcare || hasNoListingImages) &&
    (currentListing.attributes?.publicData?.findItemMethod === FindItemMethod.CannotFind ||
      hasNoStockImagesOnListing) &&
    !isTradeIn &&
    imagesFormValue?.length > 0;

  const getOriginalAndBackgroundRemovedImage = ():
    | OriginalAndBackgroundRemovedImage
    | Record<string, never> => {
    if (!editModalImage) {
      return {};
    }
    const isCurrentImageBackgroundRemoved = editModalImage.isBackgroundRemoved;
    const linkedImageIndex = allImagesUuids.indexOf(editModalImage.linkedUuid);
    const linkedImageExists = linkedImageIndex !== -1;

    if (isCurrentImageBackgroundRemoved) {
      return {
        backgroundRemovedImage: editModalImage,
        originalImage: linkedImageExists
          ? allImagesFormValue.find(
              (image: UploadcareFile) => image.uuid === editModalImage.linkedUuid
            )
          : undefined,
      };
    }
    return {
      originalImage: editModalImage,
      backgroundRemovedImage: linkedImageExists
        ? allImagesFormValue.find(
            (image: UploadcareFile) => image.uuid === editModalImage.linkedUuid
          )
        : undefined,
    };
  };

  // updates the ListingFlowForm images and allImages arrays with the newly uploaded image
  const updateListingFlowFormImagesArrays = (
    fileInfo: UploadcareFile,
    oldImageFileInfo: UploadcareFile
  ) => {
    const removedBgFileInfo: UploadcareFile = {
      ...fileInfo,
      isBackgroundRemoved: true,
      linkedUuid: oldImageFileInfo.uuid,
    };
    const updatedOldFileInfo: UploadcareFile = {
      ...oldImageFileInfo,
      linkedUuid: fileInfo.uuid,
    };
    form.mutators.update(
      ListingFlowFormField.Images,
      imageUuids.indexOf(oldImageFileInfo.uuid),
      removedBgFileInfo
    );
    form.mutators.update(
      ListingFlowFormField.AllImages,
      allImagesUuids.indexOf(oldImageFileInfo.uuid),
      updatedOldFileInfo
    );
    form.mutators.concat(ListingFlowFormField.AllImages, [removedBgFileInfo]);
    heap.trackBackgroundRemovedImageUpload(currentListing?.id?.uuid, treetId, shopName);
  };

  const uploadBackgroundRemovedImage = async (base64string: string) => {
    const blob = convertBase64toBlob(base64string);
    return uploadcare.fileFrom('object', blob);
  };

  const { backgroundRemovedImage, originalImage } = getOriginalAndBackgroundRemovedImage();

  const handleBackgroundRemovalModalSubmit = async (
    backgroundRemoveToggled: boolean,
    bgRemovedImageBase64?: string
  ) => {
    if (!editModalImage) {
      return;
    }
    const closeModal = () => {
      closeBackgroundRemovalModal();
      setEditModalImage(undefined);
      setDidUploadBackgroundRemovedImageError(false);
    };
    if (backgroundRemoveToggled && bgRemovedImageBase64) {
      setIsUploadingPhoto(true);
      // TODO TREET-4632 - refactor upload chunk from here and
      // handleCoverPhotoModalSubmit
      const [uploadedFile, uploadError] = await handle(
        uploadBackgroundRemovedImage(bgRemovedImageBase64)
      );
      if (uploadError) {
        const error = Error('Uploadcare background removed image upload failed');
        log.error(uploadError, 'upload-background-removed-image-to-uploadcare-failed', {
          image: editModalImage,
          error,
          uploadError: JSON.stringify(uploadError),
        });
        setDidUploadBackgroundRemovedImageError(true);
      } else {
        updateListingFlowFormImagesArrays(uploadedFile, editModalImage);
        closeModal();
      }
      setIsUploadingPhoto(false);
    } else {
      const imageUUID = backgroundRemoveToggled
        ? backgroundRemovedImage?.uuid
        : originalImage?.uuid;

      form.mutators.update(
        ListingFlowFormField.Images,
        imageIndex,
        allImagesFormValue.find((image: UploadcareFile) => image.uuid === imageUUID)
      );
      closeModal();
    }
  };

  const handleCoverPhotoModalSubmit = async (image: UploadcareFile, imageBase64?: string) => {
    // incoming values are an uploaded image and optionally a temporary base 64 of a background
    // removed image that we must upload first before setting as the cover photo
    if (imageBase64) {
      setIsUploadingPhoto(true);
      const [uploadedFile, uploadError] = await handle(uploadBackgroundRemovedImage(imageBase64));
      if (uploadError) {
        const error = Error('Uploadcare background removed image upload failed');
        log.error(uploadError, 'upload-background-removed-image-to-uploadcare-failed', {
          image,
          error,
          uploadError: JSON.stringify(uploadError),
        });
        setDidUploadBackgroundRemovedImageError(true);
      }
      setIsUploadingPhoto(false);
      updateListingFlowFormImagesArrays(uploadedFile, image);
      form.change(ListingFlowFormField.FeaturedImageId, uploadedFile.uuid);
      setEditModalImage(undefined);
    } else {
      form.change(ListingFlowFormField.FeaturedImageId, image.uuid);
    }
  };

  return (
    <>
      <Box display="flex" justifyContent="space-between">
        <Heading>Seller Photos</Heading>
        <Box display="flex" pt={5}>
          <InlineTextButton type="button" onClick={openPhotoTipsModal}>
            <Box display="flex" alignItems="center">
              <IconStar color={defaultTreetStyles.teal80} />
              <Typography
                component="span"
                variant="body2"
                style={{ paddingLeft: '4px', fontWeight: 600 }}
              >
                {photoTipsTitle}
              </Typography>
            </Box>
          </InlineTextButton>
        </Box>
      </Box>
      <Box
        className={css.photosUploadContainer}
        display="flex"
        flexDirection={isMobile ? 'column' : 'row'}
        justifyContent="space-between"
        mt={2}
      >
        {isMobile && (
          <PhotoRequirements imageCategoriesForCondition={imageCategoriesForCondition} />
        )}
        <Box display="flex" flex="3" px={isMobile ? 1 : 2} py={2}>
          <ChoosePhotos
            imageCategoriesForCondition={imageCategoriesForCondition}
            openEditImageModal={(uploadcareFile: UploadcareFile) => {
              setEditModalImage(uploadcareFile);
              openEditPhotoModal();
            }}
          />
        </Box>
        {!isMobile && (
          <Box className={css.photoRequirementsContainer} display="flex" flex="2">
            <PhotoRequirements imageCategoriesForCondition={imageCategoriesForCondition} />
          </Box>
        )}
      </Box>
      <PhotosError />
      <PhotoTipsModal
        isOpen={isPhotoTipsModalOpen}
        onClose={closePhotoTipsModal}
        title={photoTipsTitle}
        photoTipOptions={photoTipOptions}
      />
      {editModalImage && (
        <EditImageModal
          open={isEditPhotoModalOpen}
          onClose={closeEditPhotoModal}
          image={editModalImage}
          onEditPhotoSelected={() => {
            editPhoto(imageRatio, editModalImage, imageIndex, form);
            closeEditPhotoModal();
          }}
          onBgRemoveSelected={() => {
            openBackgroundRemovalModal();
          }}
        />
      )}
      {editModalImage && originalImage?.cdnUrl && (
        <DefaultBackgroundRemovalModal
          originalImageSrc={originalImage.cdnUrl}
          savedBackgroundRemovedImageSrc={backgroundRemovedImage?.cdnUrl}
          open={isBackgroundRemovalModalOpen}
          onClose={() => {
            setEditModalImage(undefined);
            setDidUploadBackgroundRemovedImageError(false);
          }}
          initialToggleChecked={editModalImage.isBackgroundRemoved}
          isUploading={isUploadingPhoto}
          onModalSubmit={handleBackgroundRemovalModalSubmit}
          hasUploadError={didUploadBackgroundRemovedImageError}
        />
      )}
      {shouldShowChooseCoverPhoto && (
        <ChooseCoverPhoto
          isUploading={isUploadingPhoto}
          hasUploadError={didUploadBackgroundRemovedImageError}
          imagesFormValue={imagesFormValue}
          onModalSubmit={handleCoverPhotoModalSubmit}
          onBackgroundRemovalModalClose={() => setDidUploadBackgroundRemovedImageError(false)}
        />
      )}
    </>
  );
};

export default PhotosUpload;
